import { Box, Flex, Tag, Text } from '@chakra-ui/react'

import { useUser } from '@/client/context/user'
import type { User as HasuraUser } from '@/client/services/hasura/generated/graphql'
import { KEY_CLUB_WORLD_SLUG } from '@/client/services/worlds'

import LevelFlair from '@/components/Worlds/UI/Level/LevelFlair'
import LevelIcon from '@/components/Worlds/UI/Level/LevelIcon'
import type { AugmentedWorldLevel } from '@/components/Worlds/UI/Level/types'

import type { UserDetails, WorldDetails } from '../../types'
import AnniversaryIcon from '../Anniversary/AnniversaryIcon'
import Avatar from './Avatar'
import AvatarWithIcon from './AvatarWithIcon'

type AvatarWithNameVariant = 'plain' | 'icon' | 'flair'

export const USER_FIELDS_FOR_AVATAR: (keyof UserDetails)[] = [
  'id',
  'displayName',
  'avatarUrl',
  'roles',
  'pronouns',
  'bio',
  'points',
  'username',
  'activeAnniversary',
  'keyClubDistrictName',
  'collegeInfo',
]

export type AvatarWithNameProps = {
  user:
    | Pick<
        UserDetails,
        'id' | 'displayName' | 'avatarUrl' | 'activeAnniversary' | 'roles' | 'keyClubDistrictName'
      >
    | Pick<
        HasuraUser,
        'id' | 'virtual_display_name' | 'virtual_avatar_url' | 'virtual_progression_points'
      >
  world?: Pick<WorldDetails, 'slug' | 'iconUrl' | 'primaryColor'> | null
  includeWorldIcon?: boolean
  worldLevel?: AugmentedWorldLevel | null
  variant?: AvatarWithNameVariant
  size?: 'sm' | 'md'
  omitAnniversaryTooltip?: boolean
  omitKeyClubDistrictName?: boolean
  omitAvatar?: boolean
  distinguishSelf?: boolean
}

export default function AvatarWithName({
  user,
  world,
  includeWorldIcon = false,
  worldLevel,
  variant = 'plain',
  size = 'sm',
  omitAnniversaryTooltip = false,
  omitKeyClubDistrictName = false,
  omitAvatar = false,
  distinguishSelf = false,
}: AvatarWithNameProps): JSX.Element {
  const currentUser = useUser()
  const isCurrentUser = user.id === currentUser.id

  const level = worldLevel ?? null
  const displayName = 'virtual_display_name' in user ? user.virtual_display_name : user.displayName
  const activeAnniversary = 'activeAnniversary' in user ? user.activeAnniversary : null
  const keyClubDistrictName = 'keyClubDistrictName' in user ? user.keyClubDistrictName : null

  return (
    <Flex direction="row" align="center">
      {omitAvatar ? null : includeWorldIcon && world != null ? (
        <AvatarWithIcon size={size} user={user} iconEntity={world} />
      ) : (
        <Avatar size={size} user={user} />
      )}

      <Text fontWeight="bold" whiteSpace="nowrap" ml={omitAvatar ? '0px' : '12px'}>
        {displayName}
        {distinguishSelf && isCurrentUser ? ' (You)' : ''}
      </Text>
      {!omitKeyClubDistrictName &&
      world?.slug === KEY_CLUB_WORLD_SLUG &&
      keyClubDistrictName != null ? (
        <Box ml="6px">
          <Tag bg="cyan.subtle" color="cyan.fg" whiteSpace="nowrap">
            {keyClubDistrictName}
          </Tag>
        </Box>
      ) : null}
      {activeAnniversary != null ? (
        <Box ml="6px">
          <AnniversaryIcon
            years={activeAnniversary}
            isCurrentUser={isCurrentUser}
            displayName={`${displayName}`}
            includeTooltip={!omitAnniversaryTooltip}
          />
        </Box>
      ) : variant === 'plain' ||
        (world == null && level == null) ||
        !('roles' in user) ? null : variant === 'flair' ? (
        <Box ml="12px">
          <LevelFlair user={user} worldSlug={world?.slug} level={level} />
        </Box>
      ) : (
        <Box ml="6px">
          <LevelIcon user={user} level={level} />
        </Box>
      )}
    </Flex>
  )
}
